import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { X, MapPin, Lock, Users, Globe, Star, Calendar,
         Mic, Image, Video, Play, Pause, ChevronLeft,
         AlertCircle, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import { supabase, BUCKETS } from '@/lib/supabase'
import { useAuthStore } from '@/store/authStore'
import { useQueryClient } from '@tanstack/react-query'
import {
  compressImage, compressVideo, formatBytes,
  getSupportedMimeType, getAudioExtension,
  MAX_PHOTO_B, MAX_VIDEO_B, MAX_VOICE_B,
  MAX_PHOTO_MB, MAX_VIDEO_MB, MAX_VOICE_MB,
} from '@/lib/mediaUtils'
import type { Entry } from '@/types/database'

const ENTRY_TYPES: { value: Entry['entry_type']; label: string; icon: string }[] = [
  { value: 'journal',   label: 'Journal',   icon: '📖' },
  { value: 'photo',     label: 'Photo',     icon: '📷' },
  { value: 'video',     label: 'Video',     icon: '🎥' },
  { value: 'milestone', label: 'Milestone', icon: '🏆' },
  { value: 'voice',     label: 'Voice',     icon: '🎙' },
  { value: 'location',  label: 'Location',  icon: '📍' },
]

const PRIVACY_OPTS = [
  { value: 'private' as const, icon: Lock,  label: 'Private' },
  { value: 'friends' as const, icon: Users, label: 'Friends' },
  { value: 'public'  as const, icon: Globe, label: 'Public'  },
]

const TAGS = ['Birthday','Travel','Milestone','Family','Work',
              'Health','Nature','Food','Celebration']

type MediaFile = {
  file:     File
  preview:  string
  type:     'photo' | 'video' | 'voice'
  original?: { name: string; size: number }   // set when compressed
}

// ── Mini audio player ─────────────────────────────────
function MiniAudioPlayer({ src, mimeType }: { src: string; mimeType: string }) {
  const [playing,  setPlaying]  = useState(false)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const ref = useRef<HTMLAudioElement>(null)

  const toggle = async () => {
    if (!ref.current) return
    if (playing) { ref.current.pause(); setPlaying(false) }
    else { await ref.current.play().catch(() => {}); setPlaying(true) }
  }
  const fmt = (s: number) =>
    isNaN(s) ? '0:00' : `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`

  return (
    <div className="flex items-center gap-2 p-2.5 rounded-xl bg-purple/10 border border-purple/20">
      <audio ref={ref} preload="metadata"
        onTimeUpdate={() => { setProgress(ref.current?.currentTime??0); setDuration(ref.current?.duration??0) }}
        onLoadedMetadata={() => setDuration(ref.current?.duration??0)}
        onEnded={() => { setPlaying(false); setProgress(0) }}>
        <source src={src} type={mimeType} />
      </audio>
      <button onClick={toggle}
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{background:'linear-gradient(135deg,#8b5cf6,#6d28d9)'}}>
        {playing ? <Pause size={14} className="text-white" /> : <Play size={14} className="text-white ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <input type="range" min={0} max={duration||100} value={progress}
          onChange={e => { if(ref.current){ref.current.currentTime=+e.target.value;setProgress(+e.target.value)} }}
          className="w-full h-1 accent-purple" />
        <div className="flex justify-between mt-0.5">
          <span className="font-mono text-[9px] text-white/30">{fmt(progress)}</span>
          <span className="font-mono text-[9px] text-white/30">{fmt(duration)}</span>
        </div>
      </div>
      <Mic size={12} className="text-purple flex-shrink-0" />
    </div>
  )
}

export default function CreateEntryPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const qc       = useQueryClient()

  const [title,        setTitle]        = useState('')
  const [body,         setBody]         = useState('')
  const [entryType,    setEntryType]    = useState<Entry['entry_type']>('journal')
  const [privacy,      setPrivacy]      = useState<Entry['privacy']>('private')
  const [tags,         setTags]         = useState<string[]>([])
  const [isMilestone,  setIsMilestone]  = useState(false)
  const [locationName, setLocationName] = useState('')
  const [recordedAt,   setRecordedAt]   = useState(format(new Date(), "yyyy-MM-dd'T'HH:mm"))
  const [mediaFiles,   setMediaFiles]   = useState<MediaFile[]>([])
  const [saving,       setSaving]       = useState(false)
  const [statusMsg,    setStatusMsg]    = useState('')
  const [compressProgress, setCompressProgress] = useState<number | null>(null)
  const [error,        setError]        = useState('')

  // Voice
  const [recording,    setRecording]    = useState(false)
  const [recSeconds,   setRecSeconds]   = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const timerRef         = useRef<ReturnType<typeof setInterval> | null>(null)
  const chunksRef        = useRef<BlobPart[]>([])

  // ── Add files (with compression) ─────────────────────
  const addFiles = useCallback(async (accepted: File[]) => {
    setError('')
    for (const f of accepted) {
      const isVideo = f.type.startsWith('video')
      const isPhoto = f.type.startsWith('image')

      if (isPhoto) {
        setStatusMsg(`Processing photo…`)
        try {
          const compressed = await compressImage(f)
          const wasCompressed = compressed.size < f.size
          setMediaFiles(prev => [...prev, {
            file:     compressed,
            preview:  URL.createObjectURL(compressed),
            type:     'photo',
            original: wasCompressed ? { name: f.name, size: f.size } : undefined,
          }])
        } catch {
          setMediaFiles(prev => [...prev, {
            file: f, preview: URL.createObjectURL(f), type: 'photo',
          }])
        }
        setStatusMsg('')
      } else if (isVideo) {
        if (f.size <= MAX_VIDEO_B) {
          // Small enough — add directly
          setMediaFiles(prev => [...prev, {
            file: f, preview: URL.createObjectURL(f), type: 'video',
          }])
        } else {
          // Need to compress
          setStatusMsg(`Compressing video (${formatBytes(f.size)})…`)
          setCompressProgress(0)
          try {
            const compressed = await compressVideo(f, pct => setCompressProgress(pct))
            setCompressProgress(null)
            if (compressed.size > MAX_VIDEO_B) {
              setError(
                `Video is still too large after compression (${formatBytes(compressed.size)}). ` +
                `Maximum is ${MAX_VIDEO_MB}MB. Please trim the video shorter or use a lower resolution before adding it.`
              )
            } else {
              setMediaFiles(prev => [...prev, {
                file:     compressed,
                preview:  URL.createObjectURL(compressed),
                type:     'video',
                original: { name: f.name, size: f.size },
              }])
            }
          } catch (err: any) {
            setCompressProgress(null)
            // Compression failed — try adding original with warning
            if (f.size <= MAX_VIDEO_B) {
              setMediaFiles(prev => [...prev, {
                file: f, preview: URL.createObjectURL(f), type: 'video',
              }])
            } else {
              setError(
                `Could not compress video (${formatBytes(f.size)}). ` +
                `Please trim it under ${MAX_VIDEO_MB}MB before adding.`
              )
            }
          }
          setStatusMsg('')
        }
      }
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: addFiles,
    accept: { 'image/*': [], 'video/*': [] },
    maxSize: 500 * 1024 * 1024, // 500MB — we handle compression ourselves
  })

  // ── Voice recording ───────────────────────────────────
  const startRecording = async () => {
    try {
      const stream   = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = getSupportedMimeType()
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {})
      chunksRef.current = []
      recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      recorder.onstop = () => {
        const actualMime = recorder.mimeType || mimeType || 'audio/webm'
        const ext  = getAudioExtension(actualMime)
        const blob = new Blob(chunksRef.current, { type: actualMime })
        if (blob.size > MAX_VOICE_B) {
          setError(`Voice note is too long (${formatBytes(blob.size)}). Max is ${MAX_VOICE_MB}MB. Try a shorter recording.`)
          stream.getTracks().forEach(t => t.stop())
          return
        }
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: actualMime })
        setMediaFiles(prev => [...prev, { file, preview: URL.createObjectURL(blob), type: 'voice' }])
        stream.getTracks().forEach(t => t.stop())
      }
      recorder.start(100)
      mediaRecorderRef.current = recorder
      setRecording(true)
      setRecSeconds(0)
      timerRef.current = setInterval(() => setRecSeconds(s => s + 1), 1000)
    } catch {
      setError('Microphone access denied. Please allow microphone access in your browser settings.')
    }
  }

  const stopRecording = () => {
    mediaRecorderRef.current?.stop()
    setRecording(false)
    mediaRecorderRef.current = null
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }

  const removeMedia = (i: number) => {
    URL.revokeObjectURL(mediaFiles[i].preview)
    setMediaFiles(prev => prev.filter((_, idx) => idx !== i))
  }

  const toggleTag = (tag: string) =>
    setTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])

  const fmtSecs = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

  // ── Save ──────────────────────────────────────────────
  const handleSave = async () => {
    if (!title.trim()) { setError('Please add a title.'); return }
    setError('')

    // Final size check
    for (const m of mediaFiles) {
      const limit = m.type === 'video' ? MAX_VIDEO_B : m.type === 'voice' ? MAX_VOICE_B : MAX_PHOTO_B
      if (m.file.size > limit) {
        setError(
          `"${m.file.name}" (${formatBytes(m.file.size)}) is still too large. ` +
          `Please remove it and try a shorter/smaller version.`
        )
        return
      }
    }

    setSaving(true)
    try {
      const mediaUrls: string[] = []
      for (let i = 0; i < mediaFiles.length; i++) {
        const m = mediaFiles[i]
        setStatusMsg(`Uploading ${m.type} ${i + 1} of ${mediaFiles.length} (${formatBytes(m.file.size)})…`)
        const path = `${user!.id}/${Date.now()}-${m.file.name}`
        const { data, error: upErr } = await supabase.storage
          .from(BUCKETS.MEMORIES)
          .upload(path, m.file, { upsert: true, contentType: m.file.type })
        if (upErr) throw upErr
        const { data: { publicUrl } } = supabase.storage.from(BUCKETS.MEMORIES).getPublicUrl(data.path)
        mediaUrls.push(publicUrl)
      }

      setStatusMsg('Saving entry…')
      const recordedDate = recordedAt.split('T')[0]
      const { error: insertErr } = await supabase.from('entries').insert({
        user_id:        user!.id,
        title:          title.trim(),
        body:           body.trim() || null,
        entry_type:     entryType,
        privacy,
        tags,
        media_urls:     mediaUrls,
        location_name:  locationName.trim() || null,
        is_milestone:   isMilestone,
        milestone_icon: null,
        recorded_at:    new Date(recordedAt).toISOString(),
        recorded_date:  recordedDate,
      })
      if (insertErr) throw insertErr

      qc.invalidateQueries({ queryKey: ['entries', user?.id] })
      navigate('/timeline')
    } catch (err: any) {
      const msg = err.message ?? 'Failed to save.'
      if (msg.toLowerCase().includes('size') || msg.toLowerCase().includes('limit')) {
        setError(
          `File too large for upload. Max sizes: Photos ${MAX_PHOTO_MB}MB · ` +
          `Videos ${MAX_VIDEO_MB}MB · Voice ${MAX_VOICE_MB}MB. ` +
          `Remove the large file and try again.`
        )
      } else {
        setError(msg)
      }
    } finally {
      setSaving(false)
      setStatusMsg('')
    }
  }

  const photos = mediaFiles.filter(m => m.type === 'photo')
  const videos = mediaFiles.filter(m => m.type === 'video')
  const voices = mediaFiles.filter(m => m.type === 'voice')
  const isProcessing = !!statusMsg && !saving

  return (
    <div className="min-h-dvh bg-bg flex flex-col safe-top">

      {/* Header */}
      <div className="glass sticky top-0 z-20 px-5 py-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-white/50">
          <ChevronLeft size={20} /><span className="text-sm">Cancel</span>
        </button>
        <h2 className="font-serif text-lg font-bold text-white">New Memory</h2>
        <button onClick={handleSave} disabled={saving || isProcessing || !title}
          className="text-blue-light font-semibold text-sm disabled:opacity-40">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="flex flex-col gap-5 px-5 py-5 pb-24">

        {/* Entry type */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {ENTRY_TYPES.map(t => (
            <button key={t.value} onClick={() => setEntryType(t.value)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-sm font-medium transition-all
                ${entryType===t.value ? 'border-blue bg-blue/10 text-blue-light' : 'border-white/[0.07] bg-white/5 text-white/50'}`}>
              <span>{t.icon}</span> {t.label}
            </button>
          ))}
        </div>

        <input type="text" placeholder="What happened? Give it a title…"
          value={title} onChange={e => setTitle(e.target.value)}
          className="input-field text-base font-semibold" autoFocus />

        <textarea placeholder="Tell the story…"
          value={body} onChange={e => setBody(e.target.value)}
          className="input-field min-h-[120px] resize-none" rows={5} />

        {/* ── MEDIA ── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase">Add Media</p>
            <p className="font-mono text-[9px] text-white/20">
              Videos auto-compressed if needed
            </p>
          </div>

          {/* Dropzone */}
          <div {...getRootProps()}
            className={`border-2 border-dashed rounded-2xl p-5 text-center cursor-pointer transition-all mb-3
              ${isDragActive ? 'border-blue bg-blue/5' : 'border-white/10 bg-white/[0.02]'}
              ${isProcessing ? 'pointer-events-none opacity-60' : ''}`}>
            <input {...getInputProps()} />
            <div className="flex justify-center gap-4 mb-2">
              <Image size={22} className="text-white/30" />
              <Video size={22} className="text-white/30" />
            </div>
            <p className="text-white/40 text-sm">
              {isDragActive ? 'Drop here…' : 'Tap to add photos or videos'}
            </p>
            <p className="text-white/20 text-xs mt-1 font-mono">
              Large videos are automatically compressed
            </p>
          </div>

          {/* Compression progress */}
          {isProcessing && (
            <div className="mb-3 p-4 rounded-xl bg-blue/5 border border-blue/20">
              <div className="flex items-center gap-3 mb-2">
                <Loader2 size={16} className="text-blue-light animate-spin flex-shrink-0" />
                <p className="text-blue-light text-sm font-mono">{statusMsg}</p>
              </div>
              {compressProgress !== null && (
                <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${compressProgress}%`,
                      background: 'linear-gradient(90deg,#4f6ef7,#8b5cf6)',
                    }}
                  />
                </div>
              )}
              {compressProgress !== null && (
                <p className="font-mono text-[10px] text-white/30 mt-1">{compressProgress}% processed</p>
              )}
            </div>
          )}

          {/* Voice recorder */}
          <button
            onClick={recording ? stopRecording : startRecording}
            disabled={isProcessing}
            className={`w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl border font-semibold text-sm transition-all mb-3
              ${recording ? 'border-rose/40 bg-rose/10 text-rose' : 'border-purple/20 bg-purple/5 text-purple'}
              ${isProcessing ? 'opacity-50 pointer-events-none' : ''}`}>
            <Mic size={18} className={recording ? 'animate-pulse' : ''} />
            {recording ? `⏹  Stop — ${fmtSecs(recSeconds)}` : '🎙  Record a Voice Note'}
          </button>

          {/* Photo thumbnails */}
          {photos.length > 0 && (
            <div className="mb-3">
              <p className="font-mono text-[9px] text-white/20 tracking-widest uppercase mb-2">
                📷 Photos ({photos.length})
              </p>
              <div className="flex gap-2 flex-wrap">
                {photos.map(m => (
                  <div key={m.preview} className="relative w-24 h-24 rounded-xl overflow-hidden">
                    <img src={m.preview} alt="" className="w-full h-full object-cover" />
                    {m.original && (
                      <div className="absolute bottom-0 inset-x-0 bg-green/80 text-black text-[8px] font-mono text-center py-0.5">
                        Compressed ✓
                      </div>
                    )}
                    <button onClick={() => removeMedia(mediaFiles.indexOf(m))}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center">
                      <X size={10} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Video previews */}
          {videos.length > 0 && (
            <div className="mb-3">
              <p className="font-mono text-[9px] text-white/20 tracking-widest uppercase mb-2">
                🎥 Videos ({videos.length})
              </p>
              <div className="flex flex-col gap-2">
                {videos.map(m => (
                  <div key={m.preview} className="relative rounded-xl overflow-hidden bg-black">
                    <video src={m.preview} controls className="w-full max-h-48 rounded-xl" playsInline />
                    <div className="absolute top-2 left-2 flex gap-1">
                      <div className="bg-black/70 rounded-full px-2 py-0.5 font-mono text-[9px] text-white/60">
                        {formatBytes(m.file.size)}
                      </div>
                      {m.original && (
                        <div className="bg-green/80 rounded-full px-2 py-0.5 font-mono text-[9px] text-black font-bold">
                          Compressed ✓
                        </div>
                      )}
                    </div>
                    <button onClick={() => removeMedia(mediaFiles.indexOf(m))}
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center">
                      <X size={11} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Voice notes */}
          {voices.length > 0 && (
            <div className="mb-1">
              <p className="font-mono text-[9px] text-white/20 tracking-widest uppercase mb-2">
                🎙 Voice Notes ({voices.length})
              </p>
              <div className="flex flex-col gap-2">
                {voices.map(m => (
                  <div key={m.preview} className="relative">
                    <MiniAudioPlayer src={m.preview} mimeType={m.file.type} />
                    <button onClick={() => removeMedia(mediaFiles.indexOf(m))}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/80 border border-white/10 flex items-center justify-center">
                      <X size={10} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Upload progress */}
        {saving && statusMsg && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-blue/5 border border-blue/20">
            <Loader2 size={16} className="text-blue-light animate-spin flex-shrink-0" />
            <p className="text-blue-light text-sm font-mono">{statusMsg}</p>
          </div>
        )}

        {/* Date */}
        <div className="flex items-center gap-3">
          <Calendar size={16} className="text-white/30 flex-shrink-0" />
          <input type="datetime-local" value={recordedAt}
            onChange={e => setRecordedAt(e.target.value)} className="input-field flex-1 text-sm" />
        </div>

        {/* Location */}
        <div className="relative">
          <MapPin size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text" placeholder="Add location (optional)"
            value={locationName} onChange={e => setLocationName(e.target.value)}
            className="input-field pl-10" />
        </div>

        {/* Tags */}
        <div>
          <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">Tags</p>
          <div className="flex flex-wrap gap-2">
            {TAGS.map(tag => (
              <button key={tag} onClick={() => toggleTag(tag)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all
                  ${tags.includes(tag) ? 'border-blue bg-blue/10 text-blue-light' : 'border-white/[0.07] text-white/40'}`}>
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Milestone */}
        <div className="flex items-center justify-between p-4 rounded-2xl bg-white/[0.03] border border-white/[0.07]">
          <div className="flex items-center gap-3">
            <Star size={18} className={isMilestone ? 'text-yellow-400' : 'text-white/30'} />
            <div>
              <p className="text-sm font-semibold text-white">Mark as Milestone</p>
              <p className="text-xs text-white/30">Highlighted on chronological view</p>
            </div>
          </div>
          <button onClick={() => setIsMilestone(!isMilestone)}
            className={`w-12 h-6 rounded-full transition-all relative ${isMilestone ? 'bg-yellow-500' : 'bg-white/10'}`}>
            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${isMilestone ? 'left-7' : 'left-1'}`} />
          </button>
        </div>

        {/* Privacy */}
        <div>
          <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">Privacy</p>
          <div className="flex gap-2">
            {PRIVACY_OPTS.map(({ value, icon: Icon, label }) => (
              <button key={value} onClick={() => setPrivacy(value)}
                className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl border text-xs transition-all
                  ${privacy===value ? 'border-blue bg-blue/10 text-blue-light' : 'border-white/[0.07] text-white/40'}`}>
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-rose/5 border border-rose/20">
            <AlertCircle size={18} className="text-rose flex-shrink-0 mt-0.5" />
            <p className="text-rose text-sm leading-relaxed whitespace-pre-line">{error}</p>
          </div>
        )}

      </div>
    </div>
  )
}
